import React, { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { X, Plus } from "lucide-react";
import { cn } from "@/lib/utils"; // Assuming cn utility is imported from your utils file
import { Input } from "@/components/ui/Input";
import CollapsibleTagList from "@/components/shared/CollapsibleTagList";
import { Button } from "@/components/ui/Button";
import { FORM_INPUT_STYLE } from "@/components/form/fieldStyles";

/**
 * RHFTagInput Component
 * A reusable input component for adding/removing tags, integrated with React Hook Form.
 *
 * @param {string} name - The name of the input field (used for form data).
 * @param {string} placeholder - The placeholder text for the input field.
 * @param {string} isTag - The Tag or Badge render flag
 *
 * @returns {JSX.Element} The rendered tag input field with tag management functionality.
 */
const RHFTagInput = ({ name, placeholder, isTag = false, label, disabled = false }) => {
  const {
    control,
    clearErrors,
    trigger,
    formState: { errors },
  } = useFormContext();

  const [input, setInput] = useState("");
  const [localError, setLocalError] = useState("");

  const getNestedError = (errors, path) => {
    return path.split(".").reduce((acc, key) => acc?.[key], errors);
  };

  const getRHFErrorMessage = () => {
    const fieldError = getNestedError(errors, name);
    if (!fieldError) return null;
    if (fieldError.message) return fieldError.message;
    if (Array.isArray(fieldError)) {
      const first = fieldError.find(Boolean);
      return first?.message ?? null;
    }
    return null;
  };

  const errorMessage = localError || getRHFErrorMessage();

  return (
    <div>
      {label && <label className="block text-sm mb-1.5 text-text-secondary">{label}</label>}
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          const values = field.value || []; // Get current values or set an empty array

          const addTag = async () => {
            const v = input.trim();
            if (!v) return;

            setLocalError("");

            // Case-insensitive + trimmed dedupe: block "XYZ" when "xyz" exists.
            if (values.some((x) => x.trim().toLowerCase() === v.toLowerCase())) {
              setLocalError("Already added");
              return;
            }

            // Temporarily set the new value so Zod can validate it
            const next = [...values, v];
            field.onChange([...values, v]);

            // Re-run Zod on the updated array
            const isValid = await trigger(name);

            if (!isValid) {
              // ✅ use getNestedError instead of errors[name]
              const itemErrors = getNestedError(errors, name);
              let msg = "";
              if (itemErrors?.message) {
                msg = itemErrors.message;
              } else if (Array.isArray(itemErrors)) {
                const lastErr = itemErrors[next.length - 1];
                msg = lastErr?.message ?? "Invalid skill";
              }
              setLocalError(msg);
              field.onChange(values);
              clearErrors(name);
              return;
            }

            // Valid — commit
            clearErrors(name);
            setLocalError("");
            setInput("");
          };

          const removeTag = (index) => {
            setLocalError("");
            field.onChange(values.filter((_, i) => i !== index));
            trigger(name);
          };

          return (
            <div className={cn(disabled && "opacity-50")}>
              {/* Render existing tags — removal disabled while the section is still extracting */}
              <div className="flex flex-wrap gap-2 mb-2">
                <CollapsibleTagList values={values} onRemove={disabled ? undefined : (i) => removeTag(i)} isTag={isTag} />
              </div>

              {/* Input for adding new tags */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={addTag}
                  disabled={disabled}
                  className={
                    "p-0 w-11 h-11 shrink-0 flex items-center justify-center text-text-primary bg-background-secondary border border-border-primary rounded-full transition disabled:opacity-50 disabled:cursor-not-allowed"
                  }
                >
                  <Plus size={16} />
                </Button>
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)} // Update input value on change
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} // Add tag on Enter key press
                  placeholder={placeholder}
                  disabled={disabled}
                  className={cn(FORM_INPUT_STYLE, "flex-1")}
                />
                {/* Button to add tag */}
              </div>
            </div>
          );
        }}
      />

      {errorMessage && <p className="text-red-400 text-xs mt-1">{errorMessage}</p>}
    </div>
  );
};

export default RHFTagInput;
